import type { DebouncedFunc } from 'lodash-es';
import type { ComputedRef, Ref } from 'vue';
import { debounce } from 'lodash-es';
import { computed } from 'vue';

import type { IFormSelectInputOption } from '@modules/forms/types';
import type {
  IReportChangeStructureStatePayload,
  IReportChangeTabNamePayload,
  IReportQueryOptions,
  IReportTabItem,
} from '@modules/reports/types';
import { FORM_INPUT_DEBOUNCE_DEFAULT_MS } from '@modules/forms/constants';
import { useI18n } from '@modules/i18n/composables';
import { useReportChangeStructureStateMutation, useReportChangeTabNameMutation } from '@modules/reports/mutations';
import { useReportStructureStatesQuery } from '@modules/reports/queries';

import type { IReportTabFooterEmits } from '../types';

export interface ReportTabFooterComposable {
  structureStatesOptions: ComputedRef<IFormSelectInputOption<string>[]>;
  onDebouncedChangeTabName: DebouncedFunc<(value?: string) => void>;
  inputLabelTabNameShortCode: ComputedRef<string>;
  isChangeTabNameLoading: Ref<boolean>;
  isChangeStructureStateLoading: Ref<boolean>;
  isStructureStatesLoading: Ref<boolean>;
  onTabNameChange(value?: string): Promise<void>;
  onStructureStateChange(state?: string): Promise<void>;
}

export default function useReportTabFooter(
  translationKey: Ref<symbol>,
  activeTab: Ref<IReportTabItem>,
  queryOptions: Ref<IReportQueryOptions>,
  isStructureStatesAllowed: Ref<boolean>,
  isSegmentsType: Ref<boolean>,
  emit: IReportTabFooterEmits,
): ReportTabFooterComposable {
  const { getTranslation } = useI18n();

  const { data: structureStates, isLoading: isStructureStatesLoading } = useReportStructureStatesQuery(queryOptions, isStructureStatesAllowed);

  const { mutateAsync: fetchChangeTabName, isLoading: isChangeTabNameLoading } = useReportChangeTabNameMutation();
  const { mutateAsync: fetchChangeStructureState, isLoading: isChangeStructureStateLoading } = useReportChangeStructureStateMutation();

  const structureStatesOptions = computed<IFormSelectInputOption<string>[]>(() => structureStates.value?.map(
    (value) => ({ label: getTranslation(translationKey.value, `REPORTS.STRUCTURES_TABS.${value}`).value, value }),
  ) ?? []);

  const onDebouncedChangeTabName = debounce((value?: string) => onTabNameChange(value), FORM_INPUT_DEBOUNCE_DEFAULT_MS);

  const inputLabelTabNameShortCode = computed<string>(() => (isSegmentsType.value ? 'REPORTS.SEGMENTS_TABS_NAME' : 'REPORTS.STRUCTURES_TABS_NAME'));

  async function onTabNameChange(value?: string): Promise<void> {
    if (activeTab.value.read_only)
      return;

    const payload: IReportChangeTabNamePayload = { options: queryOptions.value, tabId: activeTab.value.id };
    const name = value?.trim() || '';

    if (isSegmentsType.value) {
      payload.report_segment = { name };
    } else {
      payload.report_structure = { name };
    }

    await fetchChangeTabName(payload);

    emit('change-tab-name', name);
  }

  async function onStructureStateChange(state?: string): Promise<void> {
    if (!isStructureStatesAllowed.value || !state || activeTab.value.read_only)
      return;

    const payload: IReportChangeStructureStatePayload = {
      options: queryOptions.value,
      tabId: activeTab.value.id,
      report_structure: { aasm_state: state },
    };

    await fetchChangeStructureState(payload);

    emit('change-structure-state', state);
  }

  return {
    structureStatesOptions,
    onDebouncedChangeTabName,
    inputLabelTabNameShortCode,
    isChangeTabNameLoading,
    isChangeStructureStateLoading,
    isStructureStatesLoading,
    onTabNameChange,
    onStructureStateChange,
  };
}
